// EliminarCuenta.jsx — la página que Google Play exige enlazar desde la ficha.
//
// Play pide una URL pública que (1) nombre la app o al desarrollador, (2) explique
// los pasos para pedir la eliminación y (3) diga qué datos se borran, cuáles se
// conservan y durante cuánto tiempo. Todo lo de aquí está comprobado contra el
// código: `UserController::deleteAccount` borra de verdad (el modelo User no usa
// SoftDeletes) y las copias de seguridad se purgan a los 7 días
// (`config/backup.php`, BACKUP_RETENTION_DAYS).
import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./Terms.css"; // Reutilizar estilos de Terms

export default function EliminarCuenta() {
  return (
    <>
      <Helmet>
        <title>Eliminar tu cuenta | Wayo</title>
        <meta
          name="description"
          content="Cómo eliminar tu cuenta de Wayo y los datos asociados: pasos desde la app, por correo, qué se borra y qué se conserva."
        />
        <link rel="canonical" href="https://wayoai.app/eliminar-cuenta" />
        <meta property="og:title" content="Eliminar tu cuenta | Wayo" />
        <meta property="og:description" content="Cómo eliminar tu cuenta de Wayo y los datos asociados." />
        <meta property="og:url" content="https://wayoai.app/eliminar-cuenta" />
        <meta property="og:type" content="website" />
      </Helmet>

      <main className="terms-page">
        <Header />

        <section className="terms-content">
          <div className="terms-container">
            <h1>Eliminar tu cuenta de Wayo</h1>
            <p className="last-updated">Última actualización: 21 de septiembre de 2026</p>

            <div className="terms-section">
              <p>
                Esta página explica cómo eliminar tu cuenta de <strong>Wayo — Contador de Calorías IA</strong>,
                desarrollada por Facundo Solís, y qué pasa con tus datos. Puedes hacerlo tú
                mismo desde la app, sin pedir permiso a nadie.
              </p>
            </div>

            <div className="terms-section">
              <h2>Desde la app, en 4 pasos</h2>
              <ol>
                <li>Abre <strong>Wayo</strong> y entra en tu cuenta.</li>
                <li>Ve a la pestaña <strong>Perfil</strong>.</li>
                <li>Baja del todo y pulsa <strong>«Eliminar cuenta»</strong>, justo debajo de «Cerrar sesión».</li>
                <li>Confirma. La eliminación es <strong>inmediata y no se puede deshacer</strong>.</li>
              </ol>
            </div>

            <div className="terms-section">
              <h2>Por correo, si ya no tienes la app</h2>
              <p>
                Escribe a <strong>soporte@wayoai.app</strong> <em>desde la misma dirección con la que
                te registraste</em>, pidiendo que se elimine tu cuenta. Lo hacemos en un plazo
                máximo de <strong>30 días</strong>, normalmente mucho antes, y te confirmamos por
                correo cuando esté hecho.
              </p>
              <p>
                Por ese mismo correo puedes pedir que se borren <strong>solo algunos datos</strong> —por
                ejemplo tus fotos o tu historial de peso— sin eliminar la cuenta entera. Dinos
                qué datos quieres que quitemos.
              </p>
            </div>

            <div className="terms-section">
              <h2>Qué se elimina</h2>
              <p>Al eliminar tu cuenta se borran de nuestros servidores:</p>
              <ul>
                <li>Tu cuenta y los datos de tu perfil (nombre, correo, edad, peso, objetivos).</li>
                <li>Tus registros de comidas y los escaneos, con sus fotos.</li>
                <li>Tu historial de peso y tu progreso.</li>
                <li>Tus entrenamientos y sesiones.</li>
                <li>Tus listas de la compra, recetas favoritas y logros.</li>
                <li>Las sesiones abiertas en tus dispositivos.</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>Qué se conserva, y cuánto</h2>
              <ul>
                <li>
                  <strong>Copias de seguridad cifradas: hasta 7 días.</strong> Se borran solas al
                  caducar; no se usan para nada salvo restaurar el servicio ante una avería.
                </li>
                <li>
                  <strong>Facturas y registros de cobros:</strong> se conservan el tiempo que exige
                  la ley fiscal, sin tu perfil asociado. Esto no depende de nosotros.
                </li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>⚠️ Importante: cancela antes tu suscripción</h2>
              <p>
                Si tienes <strong>Wayo Premium</strong>, eliminar la cuenta <strong>no cancela el
                cobro</strong>: tu suscripción la gestiona Apple o Google, no nosotros. Cancélala
                antes desde tu teléfono:
              </p>
              <ul>
                <li><strong>iPhone</strong>: Ajustes → tu nombre → Suscripciones → Wayo → Cancelar.</li>
                <li><strong>Android</strong>: Google Play → tu foto → Pagos y suscripciones → Wayo → Cancelar.</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2>¿Dudas?</h2>
              <p>
                Escríbenos a <strong>soporte@wayoai.app</strong> y te contestamos. Puedes leer
                también nuestra <a href="/privacy">política de privacidad</a>.
              </p>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}
